import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios, { API } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, X, Spinner, ArrowLeft, Link as LinkIcon, Copy, Trash, Eye, ShieldCheck
} from '@phosphor-icons/react';

const ContactsPage = () => {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [formData, setFormData] = useState({});
  const [shareLabel, setShareLabel] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [contactsRes, linksRes] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/share/links`),
      ]);
      setContacts(contactsRes.data);
      setShareLinks(linksRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAddContact = async () => {
    try {
      await axios.post(`${API}/contacts`, formData);
      await fetchData();
      setShowModal(false);
      setFormData({});
      toast.success('Contact added — I\'ll keep track of them');
    } catch (err) {
      console.error(err);
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`);
      setContacts(contacts.filter(c => c.id !== id));
      toast('Contact removed');
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateShareLink = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/share/create`, { label: shareLabel || 'Shared summary' });
      await fetchData();
      setShowShareModal(false);
      setShareLabel('');
      toast.success('Share link created');
    } catch (err) {
      console.error(err);
      toast.error('Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const handleRevokeLink = async (linkId) => {
    try {
      await axios.delete(`${API}/share/${linkId}/revoke`);
      await fetchData();
      toast('Share link revoked');
    } catch (err) {
      console.error(err);
      toast.error('Failed to revoke link');
    }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/shared/${token}`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Link copied to clipboard');
    }).catch(() => {
      // Fallback
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      toast.success('Link copied');
    });
  };

  const activeLinks = shareLinks.filter(l => l.active);
  const revokedLinks = shareLinks.filter(l => !l.active);

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col">
      <header className="glass-header sticky top-0 z-10 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/profile')} className="text-slate-600 hover:text-slate-900">
          <ArrowLeft size={24} />
        </button>
        <h1 className="font-medium text-slate-900">Support Network</h1>
      </header>

      <div className="flex-1 px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size={32} className="animate-spin text-[#235347]" />
          </div>
        ) : (
          <>
            {/* Share Links Section */}
            <section className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <LinkIcon size={16} className="text-[#235347]" weight="duotone" />
                  <h2 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Shared Links
                  </h2>
                </div>
                <button
                  onClick={() => setShowShareModal(true)}
                  className="text-xs font-medium text-[#235347] hover:text-[#1A3F36] flex items-center gap-1 transition-colors"
                  data-testid="create-share-link-btn"
                >
                  <Plus size={14} />
                  New link
                </button>
              </div>

              <p className="text-xs text-slate-400 mb-3">
                Share a read-only summary of your health record with trusted people. You control access.
              </p>

              {activeLinks.length === 0 && revokedLinks.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                  <ShieldCheck size={32} className="text-slate-300 mx-auto mb-2" weight="duotone" />
                  <p className="text-sm text-slate-500">No shared links yet</p>
                  <p className="text-xs text-slate-400 mt-1">Create a link to share your health summary with family or carers</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {activeLinks.map((link) => (
                    <div key={link.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid="active-share-link">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{link.label}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-[10px] text-slate-400">Created {formatDate(link.created_at)}</span>
                            {link.view_count > 0 && (
                              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                <Eye size={10} /> {link.view_count} view{link.view_count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <button
                            onClick={() => copyLink(link.token)}
                            className="p-2 text-slate-400 hover:text-[#235347] hover:bg-slate-50 rounded-lg transition-colors"
                            title="Copy link"
                            data-testid="copy-share-link"
                          >
                            <Copy size={16} />
                          </button>
                          <button
                            onClick={() => handleRevokeLink(link.id)}
                            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            title="Revoke link"
                            data-testid="revoke-share-link"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {revokedLinks.length > 0 && (
                    <div className="mt-3">
                      <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-1.5">Revoked</p>
                      {revokedLinks.map((link) => (
                        <div key={link.id} className="bg-slate-50 border border-slate-100 rounded-xl p-3 opacity-50" data-testid="revoked-share-link">
                          <p className="text-xs text-slate-500 line-through">{link.label}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">Revoked {formatDate(link.revoked_at)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>

            {/* Contacts Section */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Users size={16} className="text-[#235347]" weight="duotone" />
                <h2 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Contacts
                </h2>
              </div>

              <div className="space-y-2">
                {contacts.map((contact) => (
                  <div key={contact.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between" data-testid="contact-item">
                    <div>
                      <h3 className="text-sm font-medium text-slate-800">{contact.name}</h3>
                      <p className="text-xs text-slate-400">{contact.relationship}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteContact(contact.id)}
                      className="text-slate-400 hover:text-red-500 transition-colors p-1"
                      data-testid="delete-contact"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
                {contacts.length === 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                    <Users size={32} className="text-slate-300 mx-auto mb-2" weight="duotone" />
                    <p className="text-sm text-slate-500">No contacts added yet</p>
                  </div>
                )}
              </div>
            </section>

            {/* FAB for adding contacts */}
            <button
              data-testid="add-contact"
              onClick={() => setShowModal(true)}
              className="fixed bottom-8 right-6 w-14 h-14 bg-[#235347] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#1A3F36] transition-colors"
            >
              <Plus size={24} />
            </button>
          </>
        )}
      </div>

      {/* Create Share Link Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full rounded-t-3xl p-6 pb-8"
            >
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-medium text-slate-900">Create Share Link</h2>
                <button onClick={() => setShowShareModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <p className="text-sm text-slate-500 mb-4">
                This will create a read-only link to your health summary. Anyone with the link can view it until you revoke it.
              </p>

              <input
                placeholder="Label (e.g. For mum, Dr Smith)"
                value={shareLabel}
                onChange={(e) => setShareLabel(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20 mb-4 text-sm"
                data-testid="share-link-label-input"
              />

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <p className="text-xs text-amber-700">
                  The shared summary includes your conditions, medications, symptoms, recent health changes, and upcoming appointments. No login details or private notes are shared.
                </p>
              </div>

              <button
                onClick={handleCreateShareLink}
                disabled={creating}
                className="w-full bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="confirm-create-share-link"
              >
                {creating ? <Spinner size={18} className="animate-spin" /> : <LinkIcon size={18} />}
                Create Link
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Contact Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full rounded-t-3xl p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-medium text-slate-900">Add Contact</h2>
                <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <input
                  placeholder="Name"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-[#235347]/20"
                  data-testid="contact-name-input"
                />
                <input
                  placeholder="Relationship (e.g., Spouse, Parent)"
                  value={formData.relationship || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                  data-testid="contact-relationship-input"
                />
                <input
                  placeholder="Email (optional)"
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                />
                <input
                  placeholder="Phone (optional)"
                  value={formData.phone || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 outline-none"
                />
              </div>

              <button
                onClick={handleAddContact}
                disabled={!formData.name || !formData.relationship}
                className="w-full mt-6 bg-[#235347] text-white rounded-full px-6 py-3 font-medium hover:bg-[#1A3F36] transition-colors disabled:opacity-50"
                data-testid="confirm-add-contact"
              >
                Add Contact
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ContactsPage;
